import type { I18nStrings } from './types.js';
import { en } from './en.js';

export type { I18nStrings };

/**
 * Lightweight i18n service for the Nova chat widget.
 *
 * English is the only built-in locale. Consumers can override any string
 * via `Partial<I18nStrings>` in the widget config. The `locale` parameter
 * is accepted for future expansion but currently has no effect beyond
 * being stored.
 */
export class I18n {
  private strings: I18nStrings;
  private readonly locale: string;

  constructor(locale?: string, overrides?: Partial<I18nStrings>) {
    this.locale = locale ?? 'en';

    // Start with English as the base (only built-in language for now)
    this.strings = { ...en };

    // Apply any consumer-provided overrides
    if (overrides) {
      const keys = Object.keys(overrides) as Array<keyof I18nStrings>;
      for (const key of keys) {
        const value = overrides[key];
        if (typeof value === 'string') {
          this.strings[key] = value;
        }
      }
    }
  }

  /** Look up a translated string by key. */
  t(key: keyof I18nStrings): string {
    return this.strings[key];
  }

  /** Return the active locale code (e.g. 'en'). */
  getLocale(): string {
    return this.locale;
  }
}
